import { useCallback, useEffect, useMemo, useState } from "react";
import type { CrmContact, CrmLead } from "@/types/crm";
import { KANBAN_COLUMNS, type KanbanStageId } from "@/components/booking/LeadsKanbanBoard";
import { fetchContact } from "@/api/crm";
import { useNavigate } from "react-router-dom";
import { listEmailThreadComments, listEmailThreads } from "@/api/email";
import type { EmailThreadComment } from "@/types/email";

type LeadDetailDrawerProps = {
  lead: (CrmLead & { _origin?: string }) | null;
  open: boolean;
  canEdit: boolean;
  onClose: () => void;
  onSave: (updates: LeadDetailFormState) => Promise<void>;
  onSendEmail?: (lead: CrmLead, contact?: CrmContact | null) => void;
  mode?: "full" | "window";
};

type LeadDetailFormState = {
  status: string;
  pipeline_stage: string;
  notes: string;
  agent_owner: string;
  title: string;
  contact_name: string;
  windowStart: string;
  windowEnd: string;
  tags: string;
};

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "confirmed", label: "Confirmed" },
  { value: "sent", label: "Sent" },
  { value: "closed", label: "Closed" },
  { value: "dismissed", label: "Dismissed" },
] as const;

export function LeadDetailDrawer({
  lead,
  open,
  canEdit,
  onClose,
  onSave,
  onSendEmail,
  mode = "full",
}: LeadDetailDrawerProps) {
  const [form, setForm] = useState<LeadDetailFormState>({
  status: "open",
  pipeline_stage: KANBAN_COLUMNS[0]?.title ?? "Prospecting",
  notes: "",
  agent_owner: "",
  title: "",
  contact_name: "",
  windowStart: "",
  windowEnd: "",
  tags: "",
});
const [saving, setSaving] = useState(false);
const [error, setError] = useState<string | null>(null);
const [contactDetails, setContactDetails] = useState<CrmContact | null>(null);
const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactExpanded, setContactExpanded] = useState(false);
  const [emailComments, setEmailComments] = useState<EmailThreadComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [commentThreadId, setCommentThreadId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!lead) return;
    const leadContact = (lead as CrmLead & { contact_name?: string | null }).contact_name;
    setForm({
      status: lead.status || "open",
      pipeline_stage: lead.pipeline_stage || (KANBAN_COLUMNS[0]?.title ?? "Prospecting"),
      notes: lead.description || "",
      agent_owner: lead.agent_owner || "",
      title: lead.title || "",
      contact_name: leadContact || "",
      windowStart: lead.event_window?.start ?? "",
      windowEnd: lead.event_window?.end ?? "",
      tags: Array.isArray(lead.metadata?.tags) ? (lead.metadata.tags as string[]).join(", ") : "",
    });
    setError(null);
    setSaving(false);
  }, [lead?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!lead?.contact_id) {
      setContactDetails(null);
      setContactError(null);
      setContactLoading(false);
      setContactExpanded(false);
      return;
    }
    setContactExpanded(false);
    setContactLoading(true);
    setContactError(null);
    fetchContact(lead.contact_id)
      .then((contact) => {
        if (cancelled) return;
        setContactDetails(contact);
      })
      .catch((exc) => {
        if (cancelled) return;
        setContactError(exc instanceof Error ? exc.message : "Unable to load contact");
      })
      .finally(() => {
        if (!cancelled) {
          setContactLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [lead?.contact_id]);

  useEffect(() => {
    let cancelled = false;
    if (!lead?.id) {
      setEmailComments([]);
      setCommentThreadId(null);
      setCommentsError(null);
      setCommentsLoading(false);
      return;
    }
    setCommentsLoading(true);
    setCommentsError(null);
    (async () => {
      try {
        const threads = await listEmailThreads({ lead_id: lead.id, limit: 1 });
        const thread = threads?.[0] ?? null;
        if (!thread) {
          if (!cancelled) {
            setCommentThreadId(null);
            setEmailComments([]);
          }
          return;
        }
        if (!cancelled) {
          setCommentThreadId(thread.id);
        }
        const comments = await listEmailThreadComments(thread.id);
        if (!cancelled) {
          setEmailComments(comments ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          setCommentsError(error instanceof Error ? error.message : "Unable to load email comments.");
          setEmailComments([]);
          setCommentThreadId(null);
        }
      } finally {
        if (!cancelled) {
          setCommentsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lead?.id]);

  const handleViewEmailsPage = useCallback(() => {
    if (!lead?.id) return;
    navigate(`/email?leadId=${lead.id}`);
  }, [lead?.id, navigate]);

  const stageOptions = useMemo(
    () =>
      KANBAN_COLUMNS.map((column) => ({
        value: column.title,
        label: column.title,
        id: column.id,
      })),
    [],
  );
  const windowOnly = mode === "window";

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save lead");
    } finally {
      setSaving(false);
    }
  };

  if (!lead) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-50 transition ${open ? "visible" : "invisible"}`}
      aria-hidden={!open}
      role="dialog"
    >
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <aside className={`absolute right-0 top-0 h-full w-full max-w-md transform bg-background/95 text-foreground/80 shadow-2xl transition-transform ${open ? "translate-x-0" : "translate-x-full"}`}>
        <form className="flex h-full flex-col" onSubmit={handleSubmit}>
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Lead detail</p>
              <h2 className="text-lg font-semibold text-foreground">{lead.title ?? `Lead #${lead.id}`}</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onSendEmail?.(lead, contactDetails)}
                className="rounded-full border border-amber-400/60 px-3 py-1 text-xs uppercase tracking-wide text-primary-foreground dark:text-amber-200 hover:bg-amber-400/10"
              >
                Email
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-border px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
              >
                Close
              </button>
            </div>
          </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="space-y-2 rounded-2xl border border-border bg-background/40 p-3">
            <button
              type="button"
              onClick={() => setContactExpanded((prev) => !prev)}
              className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-foreground/70"
            >
              <span>Contact details</span>
              <span className="text-primary-foreground dark:text-amber-200">{contactExpanded ? "−" : "+"}</span>
            </button>
            {contactExpanded && (
              <div className="space-y-2 pt-2 text-xs text-foreground/70">
                {contactLoading ? (
                  <p>Loading contact…</p>
                ) : contactError ? (
                  <p className="text-rose-300">{contactError}</p>
                ) : contactDetails ? (
                  <>
                    <p className="text-sm font-semibold text-foreground">
                      {contactDetails.contact_name || contactDetails.name}
                    </p>
                    <p>{contactDetails.company_name}</p>
                    {contactDetails.email && (
                      <p>
                        <span className="font-semibold text-muted-foreground">Email:</span>{" "}
                        <span className="text-primary-foreground dark:text-amber-200">{contactDetails.email}</span>
                      </p>
                    )}
                    {contactDetails.phone && (
                      <p>
                        <span className="font-semibold text-muted-foreground">Phone:</span>{" "}
                        <span>{contactDetails.phone}</span>
                      </p>
                    )}
                    {(contactDetails.location_city || contactDetails.location_region) && (
                      <p className="text-muted-foreground">
                        {[contactDetails.location_city, contactDetails.location_region].filter(Boolean).join(", ")}
                      </p>
                    )}
                    {!!(contactDetails.tags || []).length && (
                      <p>
                        <span className="font-semibold text-muted-foreground">Tags:</span>{" "}
                        {(contactDetails.tags || []).join(", ")}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground">No contact attached.</p>
                )}
              </div>
            )}
          </div>
          {!windowOnly && (
            <div className="grid gap-3 text-sm">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Company / Opportunity
                <input
                  type="text"
                  value={form.title}
                  onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                  disabled={!canEdit || saving}
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Primary contact
                <input
                  type="text"
                  value={form.contact_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, contact_name: event.target.value }))}
                  disabled={!canEdit || saving}
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Status
                <select
                  value={form.status}
                  onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
                  disabled={!canEdit || saving}
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Pipeline stage
                <select
                  value={form.pipeline_stage}
                  onChange={(event) => setForm((prev) => ({ ...prev, pipeline_stage: event.target.value }))}
                  disabled={!canEdit || saving}
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                >
                  {stageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Assigned agent
                <input
                  type="text"
                  value={form.agent_owner}
                  onChange={(event) => setForm((prev) => ({ ...prev, agent_owner: event.target.value }))}
                  disabled={!canEdit || saving}
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Notes
                <textarea
                  rows={6}
                  value={form.notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                  disabled={!canEdit || saving}
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground resize-y"
                />
              </label>
            </div>
          )}
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Window start
              <input
                type="date"
                value={form.windowStart}
                onChange={(event) => setForm((prev) => ({ ...prev, windowStart: event.target.value }))}
                disabled={!canEdit || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Window end
              <input
                type="date"
                value={form.windowEnd}
                onChange={(event) => setForm((prev) => ({ ...prev, windowEnd: event.target.value }))}
                disabled={!canEdit || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
            </label>
          </div>
          {!windowOnly && (
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Tags (comma separated)
              <input
                type="text"
                value={form.tags}
                placeholder="Winter Tour 25, Festival"
                onChange={(event) => setForm((prev) => ({ ...prev, tags: event.target.value }))}
                disabled={!canEdit || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
            </label>
          )}
          <div className="space-y-1 rounded-2xl border border-border bg-background/40 p-3 text-xs text-foreground/70">
            <div className="flex items-center justify-between uppercase tracking-wide text-muted-foreground">
              <span>Email comments</span>
              <button
                type="button"
                onClick={handleViewEmailsPage}
                className="text-primary-foreground dark:text-amber-200 hover:text-primary-foreground dark:text-amber-100"
              >
                View in email page
              </button>
            </div>
            {commentsLoading ? (
              <p className="text-muted-foreground">Loading comments…</p>
            ) : commentsError ? (
              <p className="text-rose-300">{commentsError}</p>
            ) : emailComments.length === 0 ? (
              <p className="text-muted-foreground">No email comments yet.</p>
            ) : (
              <div className="space-y-2">
                {emailComments.map((comment) => (
                  <div
                    key={comment.id}
                    className="space-y-1 rounded-lg border border-border/60 bg-background/30 px-3 py-2"
                  >
                    <p className="text-xs text-foreground">{comment.text}</p>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {comment.author_name ?? "Comment"} ·{" "}
                      {comment.created_at ? new Date(comment.created_at).toLocaleString() : "Unknown time"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          {!canEdit && !windowOnly && (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-primary-foreground dark:text-amber-200">
              This lead originates from booking recommendations and cannot be edited until it’s synced to CRM.
            </p>
          )}
          {error && <p className="text-xs text-rose-300">{error}</p>}
        </div>
        <div className="border-t border-border px-5 py-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-semibold text-foreground/70 hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canEdit || saving}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-gold/60 bg-gold/10 px-4 py-2 text-sm font-semibold text-gold transition hover:bg-gold/20 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save lead"}
            </button>
          </div>
        </div>
        </form>
      </aside>
    </div>
  );
}

export type { LeadDetailFormState };
